import { env } from "@/env";
import {
  AdminWorkflowStats,
  AdminWorkflowStatsSchema,
  AdminRequirementDetail,
  AdminRequirementDetailSchema,
  CollectionType,
  ComplianceCase,
  ComplianceCaseSchema,
  ComplianceDocumentType,
  ComplianceDocumentTypeSchema,
  ComplianceOverview,
  ComplianceOverviewSchema,
  CursorPaginatedResult,
  parseCursorResult,
} from "@/types/compliance";
import { z } from "zod/v4";
import { COMMON_FETCH_HEADERS } from "./utils";
import { parseApiErrorFromResponse } from "./api-errors";

const MAX_UPLOAD_SIZE_BYTES = 50 * 1024 * 1024;

type Method = "GET" | "POST" | "PATCH" | "DELETE";

export class ComplianceApiError extends Error {
  constructor(
    message: string,
    readonly statusCode: number,
    readonly tag?: string,
  ) {
    super(message);
    this.name = "ComplianceApiError";
  }
}

const baseUrl = env.VITE_MERCHANT_PORTAL_BACKEND_URL;

const parseJson = async (response: Response) => {
  const text = await response.text();
  if (!text) {
    return null;
  }
  return JSON.parse(text) as unknown;
};

async function request<T>({
  accessToken,
  path,
  method,
  body,
  query,
  applicationId,
  parser,
  signal,
}: {
  accessToken: string;
  path: string;
  method: Method;
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined>;
  applicationId?: string;
  parser?: (json: unknown) => T;
  signal?: AbortSignal;
}) {
  const search = new URLSearchParams();
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value === undefined || value === null || value === "") {
        continue;
      }
      search.set(key, String(value));
    }
  }

  const response = await fetch(
    `${baseUrl}${path}${search.size > 0 ? `?${search.toString()}` : ""}`,
    {
      method,
      signal,
      headers: {
        Authorization: `Bearer ${accessToken}`,
        ...(body ? { "Content-Type": "application/json" } : {}),
        ...(applicationId ? { "x-application-id": applicationId } : {}),
        ...COMMON_FETCH_HEADERS,
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    },
  );

  if (!response.ok) {
    const apiError = await parseApiErrorFromResponse(response);
    throw new ComplianceApiError(
      apiError.message,
      apiError.statusCode ?? response.status,
      apiError.tag,
    );
  }

  if (!parser) {
    return undefined as T;
  }

  const json = await parseJson(response);
  return parser(json);
}

const PresignResponseSchema = z.object({
  uploadToken: z.string(),
  uploadId: z.string(),
  url: z.string(),
  requiredHeaders: z.record(z.string(), z.string()).default({}),
  expiresInSeconds: z.number(),
});
const RequiringActionCountSchema = z.object({
  count: z.number(),
  waitingForInput: z.number(),
  partial: z.number(),
  pendingVolumeReview: z.number(),
  approved: z.number(),
});
const PaymentComplianceCaseLookupSchema = z.object({
  caseId: z.string().nullable(),
});

export const complianceApi = {
  maxUploadSizeBytes: MAX_UPLOAD_SIZE_BYTES,

  merchant: {
    getRequiringActionCount: ({
      accessToken,
      applicationId,
    }: {
      accessToken: string;
      applicationId: string;
    }): Promise<{
      count: number;
      waitingForInput: number;
      partial: number;
      pendingVolumeReview: number;
      approved: number;
    }> =>
      request({
        accessToken,
        applicationId,
        path: "/api/compliance/outstanding-actions/count",
        method: "GET",
        parser: (json) => RequiringActionCountSchema.parse(json),
      }),

    getCases: ({
      accessToken,
      applicationId,
      limit,
      cursor,
      workflowStatuses,
      signal,
    }: {
      accessToken: string;
      applicationId: string;
      limit?: number;
      cursor?: string;
      workflowStatuses?: string;
      signal?: AbortSignal;
    }): Promise<CursorPaginatedResult<ComplianceCase>> =>
      request({
        accessToken,
        applicationId,
        path: "/api/compliance/cases",
        method: "GET",
        query: { limit, cursor, workflowStatuses },
        signal,
        parser: (json) => parseCursorResult(json, ComplianceCaseSchema),
      }),

    getCaseById: ({
      accessToken,
      applicationId,
      caseId,
    }: {
      accessToken: string;
      applicationId: string;
      caseId: string;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/cases/${caseId}`,
        method: "GET",
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),

    getPaymentComplianceCase: ({
      accessToken,
      applicationId,
      paymentId,
    }: {
      accessToken: string;
      applicationId: string;
      paymentId: string;
    }): Promise<{ caseId: string | null }> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/payments/${paymentId}/case`,
        method: "GET",
        parser: (json) => PaymentComplianceCaseLookupSchema.parse(json),
      }),

    presignUpload: ({
      accessToken,
      applicationId,
      requirementId,
      originalFileName,
      contentType,
      sizeBytes,
    }: {
      accessToken: string;
      applicationId: string;
      requirementId: string;
      originalFileName: string;
      contentType: string;
      sizeBytes: number;
    }): Promise<{
      uploadToken: string;
      uploadId: string;
      uploadUrl: string;
      headers: Record<string, string>;
      expiresInSeconds: number;
    }> => {
      if (sizeBytes > MAX_UPLOAD_SIZE_BYTES) {
        throw new ComplianceApiError(
          "File is larger than 50MB",
          400,
          "FILE_TOO_LARGE",
        );
      }

      return request({
        accessToken,
        applicationId,
        path: `/api/compliance/requirements/${requirementId}/uploads/presign`,
        method: "POST",
        body: { originalFileName, contentType, sizeBytes },
        parser: (json) => {
          const parsed = PresignResponseSchema.parse(json);
          return {
            uploadToken: parsed.uploadToken,
            uploadId: parsed.uploadId,
            uploadUrl: parsed.url,
            headers: parsed.requiredHeaders,
            expiresInSeconds: parsed.expiresInSeconds,
          };
        },
      });
    },

    completeUpload: ({
      accessToken,
      applicationId,
      requirementId,
      uploadToken,
    }: {
      accessToken: string;
      applicationId: string;
      requirementId: string;
      uploadToken: string;
    }): Promise<void> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/requirements/${requirementId}/uploads/complete`,
        method: "POST",
        body: { uploadToken },
      }),

    getUploadDownloadUrl: ({
      accessToken,
      applicationId,
      uploadId,
    }: {
      accessToken: string;
      applicationId: string;
      uploadId: string;
    }): Promise<{ url: string }> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/uploads/${uploadId}/download-url`,
        method: "GET",
        parser: (json) => z.object({ url: z.string() }).parse(json),
      }),

    deleteUpload: ({
      accessToken,
      applicationId,
      uploadId,
    }: {
      accessToken: string;
      applicationId: string;
      uploadId: string;
    }): Promise<void> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/uploads/${uploadId}`,
        method: "DELETE",
      }),

    submitRequirement: ({
      accessToken,
      applicationId,
      requirementId,
    }: {
      accessToken: string;
      applicationId: string;
      requirementId: string;
    }): Promise<void> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/requirements/${requirementId}/submit`,
        method: "POST",
      }),

    presignSupportingInfo: ({
      accessToken,
      applicationId,
      caseId,
      sizeBytes,
    }: {
      accessToken: string;
      applicationId: string;
      caseId: string;
      sizeBytes: number;
    }): Promise<{
      uploadToken: string;
      uploadId: string;
      url: string;
      requiredHeaders: Record<string, string>;
      expiresInSeconds: number;
    }> => {
      if (sizeBytes > MAX_UPLOAD_SIZE_BYTES) {
        throw new ComplianceApiError(
          "File is larger than 50MB",
          400,
          "FILE_TOO_LARGE",
        );
      }
      return request({
        accessToken,
        applicationId,
        path: `/api/compliance/cases/${caseId}/supporting-information/presign`,
        method: "POST",
        body: { sizeBytes },
        parser: (json) => {
          const parsed = PresignResponseSchema.parse(json);
          return {
            uploadToken: parsed.uploadToken,
            uploadId: parsed.uploadId,
            url: parsed.url,
            requiredHeaders: parsed.requiredHeaders,
            expiresInSeconds: parsed.expiresInSeconds,
          };
        },
      });
    },

    submitForReview: ({
      accessToken,
      applicationId,
      caseId,
      merchantSupportUploadToken,
    }: {
      accessToken: string;
      applicationId: string;
      caseId: string;
      merchantSupportUploadToken?: string;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/cases/${caseId}/submit-for-review`,
        method: "POST",
        body: merchantSupportUploadToken ? { merchantSupportUploadToken } : {},
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),

    unsubmitForReview: ({
      accessToken,
      applicationId,
      caseId,
    }: {
      accessToken: string;
      applicationId: string;
      caseId: string;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/cases/${caseId}/unsubmit-for-review`,
        method: "POST",
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),

    getSupportingInfoDownloadUrl: ({
      accessToken,
      applicationId,
      caseId,
      uploadId,
    }: {
      accessToken: string;
      applicationId: string;
      caseId: string;
      uploadId: string;
    }): Promise<{ url: string }> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/cases/${caseId}/supporting-information/${uploadId}/download-url`,
        method: "GET",
        parser: (json) => z.object({ url: z.string() }).parse(json),
      }),

    deleteSupportingInfo: ({
      accessToken,
      applicationId,
      caseId,
      uploadId,
    }: {
      accessToken: string;
      applicationId: string;
      caseId: string;
      uploadId: string;
    }): Promise<void> =>
      request({
        accessToken,
        applicationId,
        path: `/api/compliance/cases/${caseId}/supporting-information/${uploadId}`,
        method: "DELETE",
      }),
  },

  admin: {
    getOverview: ({
      accessToken,
    }: {
      accessToken: string;
    }): Promise<ComplianceOverview> =>
      request({
        accessToken,
        path: "/api/admin/compliance/overview",
        method: "GET",
        parser: (json) => ComplianceOverviewSchema.parse(json),
      }),

    getOverviewWorkflowStats: ({
      accessToken,
    }: {
      accessToken: string;
    }): Promise<AdminWorkflowStats> =>
      request({
        accessToken,
        path: "/api/admin/compliance/overview/workflow-stats",
        method: "GET",
        parser: (json) => AdminWorkflowStatsSchema.parse(json),
      }),

    getCases: ({
      accessToken,
      limit,
      cursor,
      merchantId,
      applicationId,
      customerName,
      workflowStatuses,
    }: {
      accessToken: string;
      limit?: number;
      cursor?: string;
      merchantId?: string;
      applicationId?: string;
      customerName?: string;
      workflowStatuses?: string;
    }): Promise<CursorPaginatedResult<ComplianceCase>> =>
      request({
        accessToken,
        path: "/api/admin/compliance/cases",
        method: "GET",
        query: {
          limit,
          cursor,
          merchantId,
          applicationId,
          customerName,
          workflowStatuses,
        },
        parser: (json) => parseCursorResult(json, ComplianceCaseSchema),
      }),

    getCaseById: ({
      accessToken,
      caseId,
    }: {
      accessToken: string;
      caseId: string;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}`,
        method: "GET",
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),

    uploadCaseToOptimus: ({
      accessToken,
      caseId,
    }: {
      accessToken: string;
      caseId: string;
    }): Promise<{ queued: boolean }> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/upload-to-optimus`,
        method: "POST",
        parser: (json) => z.object({ queued: z.boolean() }).parse(json),
      }),

    addRequirementToCase: ({
      accessToken,
      caseId,
      requirementCode,
    }: {
      accessToken: string;
      caseId: string;
      requirementCode: string;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/requirements`,
        method: "POST",
        body: { requirementCode },
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),

    getRequirement: ({
      accessToken,
      requirementId,
    }: {
      accessToken: string;
      requirementId: string;
    }): Promise<AdminRequirementDetail> =>
      request({
        accessToken,
        path: `/api/admin/compliance/requirements/${requirementId}`,
        method: "GET",
        parser: (json) => AdminRequirementDetailSchema.parse(json),
      }),

    approveRequirement: ({
      accessToken,
      requirementId,
    }: {
      accessToken: string;
      requirementId: string;
    }): Promise<void> =>
      request({
        accessToken,
        path: `/api/admin/compliance/requirements/${requirementId}/approve`,
        method: "POST",
      }),

    rejectRequirement: ({
      accessToken,
      requirementId,
      reason,
    }: {
      accessToken: string;
      requirementId: string;
      reason: string;
    }): Promise<void> =>
      request({
        accessToken,
        path: `/api/admin/compliance/requirements/${requirementId}/reject`,
        method: "POST",
        body: { reason },
      }),

    listDocumentTypes: ({
      accessToken,
      includeInactive,
    }: {
      accessToken: string;
      includeInactive: boolean;
    }): Promise<ComplianceDocumentType[]> =>
      request({
        accessToken,
        path: "/api/admin/compliance/document-types",
        method: "GET",
        query: { includeInactive },
        parser: (json) => z.array(ComplianceDocumentTypeSchema).parse(json),
      }),

    createDocumentType: ({
      accessToken,
      code,
      displayName,
      description,
      supporting,
      collectionType,
    }: {
      accessToken: string;
      code: string;
      displayName: string;
      description?: string | null;
      supporting?: string[];
      collectionType: CollectionType;
    }): Promise<ComplianceDocumentType> =>
      request({
        accessToken,
        path: "/api/admin/compliance/document-types",
        method: "POST",
        body: {
          code,
          displayName,
          collectionType,
          ...(description != null && { description }),
          ...(supporting !== undefined && { supporting }),
        },
        parser: (json) => ComplianceDocumentTypeSchema.parse(json),
      }),

    updateDocumentType: ({
      accessToken,
      code,
      displayName,
      description,
      supporting,
      isActive,
      collectionType,
    }: {
      accessToken: string;
      code: string;
      displayName?: string;
      description?: string | null;
      supporting?: string[];
      isActive?: boolean;
      collectionType?: CollectionType;
    }): Promise<ComplianceDocumentType> =>
      request({
        accessToken,
        path: `/api/admin/compliance/document-types/${code}`,
        method: "PATCH",
        body: {
          ...(displayName !== undefined && { displayName }),
          ...(description !== undefined && {
            description: description ?? null,
          }),
          ...(supporting !== undefined && { supporting }),
          ...(isActive !== undefined && { isActive }),
          ...(collectionType !== undefined && { collectionType }),
        },
        parser: (json) => ComplianceDocumentTypeSchema.parse(json),
      }),

    deactivateDocumentType: ({
      accessToken,
      code,
    }: {
      accessToken: string;
      code: string;
    }): Promise<void> =>
      request({
        accessToken,
        path: `/api/admin/compliance/document-types/${code}/deactivate`,
        method: "POST",
      }),

    reactivateDocumentType: ({
      accessToken,
      code,
    }: {
      accessToken: string;
      code: string;
    }): Promise<ComplianceDocumentType> =>
      request({
        accessToken,
        path: `/api/admin/compliance/document-types/${code}/reactivate`,
        method: "POST",
        parser: (json) => ComplianceDocumentTypeSchema.parse(json),
      }),

    getUploadDownloadUrl: ({
      accessToken,
      uploadId,
    }: {
      accessToken: string;
      uploadId: string;
    }): Promise<{ url: string }> =>
      request({
        accessToken,
        path: `/api/admin/compliance/uploads/${uploadId}/download-url`,
        method: "GET",
        parser: (json) => z.object({ url: z.string() }).parse(json),
      }),

    getCaseDownloadUrls: ({
      accessToken,
      caseId,
    }: {
      accessToken: string;
      caseId: string;
    }): Promise<
      {
        uploadId: string;
        url: string;
        documentTypeCode: string;
        documentTypeDisplayName: string;
        originalFileName: string;
        sizeBytes: number;
        uploadedAtIso: string;
      }[]
    > =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/download-urls`,
        method: "GET",
        parser: (json) =>
          z
            .array(
              z.object({
                uploadId: z.string(),
                url: z.string(),
                documentTypeCode: z.string(),
                documentTypeDisplayName: z.string(),
                originalFileName: z.string(),
                sizeBytes: z.number(),
                uploadedAtIso: z.string(),
              }),
            )
            .parse(json),
      }),

    createCase: ({
      accessToken,
      applicationId,
      merchantId,
      merchantName,
      customerFullName,
      requirementCodes,
      transactionVolume,
      transactionVolumeCurrency,
      transactionCount,
      customerMetadata,
    }: {
      accessToken: string;
      applicationId: string;
      merchantId: string;
      merchantName: string;
      customerFullName: string;
      requirementCodes: string[];
      transactionVolume?: string;
      transactionVolumeCurrency?: string;
      transactionCount?: number;
      customerMetadata?: Record<string, string>;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        path: "/api/admin/compliance/cases",
        method: "POST",
        body: {
          applicationId,
          merchantId,
          merchantName,
          customerFullName,
          requirementCodes,
          ...(transactionVolume != null && { transactionVolume }),
          ...(transactionVolumeCurrency != null && {
            transactionVolumeCurrency,
          }),
          ...(transactionCount != null && { transactionCount }),
          customerMetadata,
        },
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),

    deleteCase: ({
      accessToken,
      caseId,
    }: {
      accessToken: string;
      caseId: string;
    }): Promise<void> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}`,
        method: "DELETE",
      }),

    presignSupportingInfo: ({
      accessToken,
      caseId,
      supportingType,
      sizeBytes,
    }: {
      accessToken: string;
      caseId: string;
      supportingType:
        | "volume_supporting_information"
        | "merchant_supporting_information";
      sizeBytes: number;
    }): Promise<{
      uploadToken: string;
      uploadId: string;
      url: string;
      requiredHeaders: Record<string, string>;
      expiresInSeconds: number;
    }> => {
      if (sizeBytes > MAX_UPLOAD_SIZE_BYTES) {
        throw new ComplianceApiError(
          "File is larger than 50MB",
          400,
          "FILE_TOO_LARGE",
        );
      }
      return request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/supporting-information/presign`,
        method: "POST",
        body: { supportingType, sizeBytes },
        parser: (json) => {
          const parsed = PresignResponseSchema.parse(json);
          return {
            uploadToken: parsed.uploadToken,
            uploadId: parsed.uploadId,
            url: parsed.url,
            requiredHeaders: parsed.requiredHeaders,
            expiresInSeconds: parsed.expiresInSeconds,
          };
        },
      });
    },

    completeSupportingInfo: ({
      accessToken,
      caseId,
      uploadToken,
    }: {
      accessToken: string;
      caseId: string;
      uploadToken: string;
    }): Promise<void> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/supporting-information/complete`,
        method: "POST",
        body: { uploadToken },
      }),

    deleteSupportingInfo: ({
      accessToken,
      caseId,
      uploadId,
    }: {
      accessToken: string;
      caseId: string;
      uploadId: string;
    }): Promise<void> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/supporting-information/${uploadId}`,
        method: "DELETE",
      }),

    approveCase: ({
      accessToken,
      caseId,
    }: {
      accessToken: string;
      caseId: string;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/approve`,
        method: "POST",
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),

    revokeApproval: ({
      accessToken,
      caseId,
    }: {
      accessToken: string;
      caseId: string;
    }): Promise<ComplianceCase> =>
      request({
        accessToken,
        path: `/api/admin/compliance/cases/${caseId}/revoke-approval`,
        method: "POST",
        parser: (json) => ComplianceCaseSchema.parse(json),
      }),
  },
};
